import { ArticleInfo } from "../entities/ArticleInfo";
import { DocumentInfo } from "../entities/DocumentInfo";
import { MovieInfo } from "../entities/MovieInfo";
import { LiveInfo } from "../entities/LiveInfo";

// 自身がこのコンテンツのオーナー(講師)かどうか
export const isOwnerContent = (content?: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => {
  if (!content) {
    return false;
  }
  return content.isOwner;
};

// 契約済みか
export const isContractContent = (content?: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => {
  if (!content) {
    return false;
  }
  if (!content.isContract) {
    return false;
  }

  return isContractPlan(content.plan);
};

// 仮解約中か
export const isProvisionalCancellationContent = (content?: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => {
  if (!content) {
    return false;
  }
  return isProvisionalCancellationPlan(content.plan);
};

// コンテンツを見ることができるかどうか
export const isShowableContent = (content?: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => {
  if (!content) {
    return false;
  }

  if (isOwnerContent(content)) {
    return true;
  }

  if (content.publishStatus === "PRIVATE") {
    return false;
  }

  if (content.isFree) {
    return true;
  }

  if (isContractContent(content)) {
    return true;
  }
  return false;
};

// ダウンロード可能か
export const isDownloadableContent = (content?: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => {
  if (!content) {
    return false;
  }
  if (content.contentsType === "document") {
    return content.plan?.isDownloadableDocument ?? false;
  } else if (content?.contentsType === "movie" || content?.contentsType === "archive_movie") {
    return content.plan?.isDownloadableVideo ?? false;
  }
  return false;
};

// チャプター対応かどうか
export const isChapterSupportedContentType = (contentsType: string) => {
  return contentsType === "movie" || contentsType === "archive_movie";
};

// チャプター対応かどうか
export const isChapterSupportedDistributionType = (distributionType: string) => {
  return distributionType === "movie" || distributionType === "livearchive";
};

// プラン制限によってコンテンツがブロックされているかどうか
export const isContentBlockedByPlanLimit = (content?: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo | null) => {
  if (!content) {
    return false;
  }
  // 講座には契約済みだが、このコンテンツが要求する限定プランには未契約
  return isContractPlan(content.plan) && content.isContract === false && content.limitedPlanPriceIds?.length > 0;
};

/**
 * コンテンツがプラン価格によって制限されているか判定
 *
 * @param limitedPlanPriceIds コンテンツの限定プランID一覧
 * @param userPlanPriceId ユーザーのプランID
 * @param isOwner ユーザーがオーナーかどうか
 * @returns プラン価格によって制限されている場合true
 */
export const isContentLimitedByPlanPrice = (
  limitedPlanPriceIds: number[] | undefined,
  userPlanPriceId: number | undefined,
  isOwner: boolean,
): boolean => {
  // オーナーの場合は常に閲覧可能
  if (isOwner) return false;

  // 限定プランIDが設定されていない場合は制限なし
  if (!limitedPlanPriceIds || limitedPlanPriceIds.length === 0) return false;

  // ユーザーのプランIDが含まれていない場合は制限あり
  return !userPlanPriceId || !limitedPlanPriceIds.includes(userPlanPriceId);
};

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    isOwnerContent: (content: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => boolean;
    isContractContent: (content: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => boolean;
    isShowableContent: (content: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => boolean;
    isProvisionalCancellationContent: (content: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => boolean;
    isDownloadableContent: (content: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo) => boolean;
    isChapterSupportedContentType: (contentsType: string) => boolean;
    isChapterSupportedDistributionType: (distributionType: string) => boolean;
    isContentBlockedByPlanLimit: (content: ArticleInfo | DocumentInfo | MovieInfo | LiveInfo | null) => boolean;
    isContentLimitedByPlanPrice: (
      limitedPlanPriceIds: number[] | undefined,
      userPlanPriceId: number | undefined,
      isOwner: boolean,
    ) => boolean;
  }
}
